import React, { useState } from 'react';
import { useIsMobile } from '@guildeducationinc/catalog-tools';
import { MxButton } from '@guildeducationinc/playground-mx';
import { IconButton } from '@guildeducationinc/recess';
import BookmarkIcon from '@guildeducationinc/recess/icons/BookmarkIcon';
import BookmarkBorderIcon from '@guildeducationinc/recess/icons/BookmarkBorderIcon';
import { useBookmarkActions } from 'src/hooks/useBookmarkActions';
import { isBookmarked } from 'src/utilities/offeringHitsUtility';
import { useBookmarks } from 'src/components/Providers/BookmarksProvider';

interface Props {
	apName?: string;
	className?: string;
	hitName?: string;
	offeringId: string;
	theme?: 'dark' | 'light';
	variant?: 'iconOnly' | 'button';
}

export const BookmarkButton: React.FC<Props> = ({
	apName,
	className,
	hitName,
	offeringId,
	theme,
	variant,
}) => {
	const { bookmarks } = useBookmarks();
	const { updateBookmark } = useBookmarkActions();
	const bookmarked = isBookmarked(offeringId, bookmarks);
	const isMobile = useIsMobile();
	const [isDisabled, setIsDisabled] = useState<boolean>(false);

	const handleClick = async e => {
		e.preventDefault();
		setIsDisabled(true);

		try {
			await updateBookmark(offeringId);
			setIsDisabled(false);
		} catch (err) {
			setIsDisabled(false);
		}
	};

	const textButton = (
		<MxButton
			appearance='outline'
			aria-label={`Bookmark ${hitName} from ${apName}`}
			className={className}
			disabled={isDisabled}
			icon={bookmarked ? <BookmarkIcon size={18} /> : <BookmarkBorderIcon size={18} />}
			iconDirection='right'
			label={bookmarked ? 'Bookmarked' : 'Not bookmarked'}
			onClick={handleClick}
			testid='bookmark-text-button'
			theme={theme}
			title='Bookmark this program'
		>
			{bookmarked ? 'Bookmarked' : 'Bookmark'}
		</MxButton>
	);

	const iconButton = (
		<IconButton
			appearance='ghost'
			aria-label={`Bookmark ${hitName} from ${apName}`}
			className={className}
			data-testid='bookmark-button'
			disabled={isDisabled}
			icon={
				bookmarked ? (
					<BookmarkIcon size={isMobile ? 24 : 32} />
				) : (
					<BookmarkBorderIcon size={isMobile ? 24 : 32} />
				)
			}
			label={bookmarked ? 'Bookmarked' : 'Not bookmarked'}
			onClick={handleClick}
			style={{ verticalAlign: 'middle', padding: '12px' }}
			title='Bookmark this program'
		/>
	);

	return variant === 'iconOnly' ? iconButton : textButton;
};
